// src/config/monetization.js
/**
 * Single source of truth for all monetization rules.
 *
 * Pricing tiers, data-source toggles, and Kleido Coin economics all live here
 * so that the subscription page, settings page, and any future monetization
 * UI (analytics, payouts) all read from the same numbers.
 *
 * To re-price a feature: edit FEATURE_PRICING.
 * To add a new data source: add it to DATA_SOURCES with a default opt-in value.
 * To change coin economics: edit COIN_ECONOMICS.
 */

// ----- Feature pricing (unchanged from original) -----
export const FEATURE_PRICING = {
    CD:      { min: 1.99, max: 2.99, label: "$1.99-$2.99/mo" },
    IDN:     { min: 0,    max: 5,    label: "Free-$5/mo"      },
    INST:    { min: 1.99, max: 3.99, label: "$1.99-$3.99/mo" },
    OOTSHAV: { min: 0.99, max: 0.99, label: "$0.99/mo"        },
    DELUZON: { min: 1.99, max: 2.99, label: "$1.99-$2.99/mo" },
    KLAMITY: { min: 0,    max: 0,    label: "FREE"            },
  };
  
  export const FEATURE_NAMES = {
    CD:      "Community Driven",
    IDN:     "Interest Driven Social Network",
    INST:    "Instants Creation Tool",
    OOTSHAV: "Event Social Network (Ootshav)",
    DELUZON: "Credibility Reviews (Deluzon)",
    KLAMITY: "Climate/Disaster Network",
    LS:      "Life Story",
    FRIEND:  "Friends Network",
    GROUP:   "Groups",
    ARCHIVE: "Archive",
    TREND:   "Trending",
  };
  
  export const ALL_PAID_FEATURES = ["CD", "IDN", "INST", "OOTSHAV", "DELUZON"];
  
  // ----- Default-subscription toggle -----
  // When ON, every newly added feature is auto-subscribed unless the user
  // explicitly opts out on this page. When OFF, every feature requires explicit
  // subscription (current behavior).
  export const DEFAULT_SUBSCRIPTION = {
    STORAGE_KEY: "monetization.defaultSubscription",
    DEFAULT: false,  // opt-in by default — safer for privacy
  };
  
  // ----- Data sources the user can opt out of for dataset generation -----
  // `defaultOptIn: true`  → user must actively toggle OFF to exclude
  // `defaultOptIn: false` → user must actively toggle ON to include
  // `sensitive: true`     → marked with a stronger warning in the UI
  export const DATA_SOURCES = {
    PRIVATE: {
      id: "PRIVATE",
      label: "Private Data",
      description: "Name, location, profile photo, contact details.",
      icon: "🔒",
      defaultOptIn: true,
      sensitive: true,
    },
    BEHAVIORAL: {
      id: "BEHAVIORAL",
      label: "Behavioral Data",
      description: "Pages visited, time spent, scroll depth, click patterns.",
      icon: "🧭",
      defaultOptIn: true,
      sensitive: false,
    },
    EMOTIONAL: {
      id: "EMOTIONAL",
      label: "Emotional Data",
      description: "Inferred mood, sentiment from posts and reactions.",
      icon: "💗",
      defaultOptIn: false,
      sensitive: true,
    },
    WEATHER: {
      id: "WEATHER",
      label: "Weather & Prediction Data",
      description: "Local weather, forecasts, climate signals from your region.",
      icon: "🌦️",
      defaultOptIn: true,
      sensitive: false,
    },
    EVENT: {
      id: "EVENT",
      label: "Event Data",
      description: "Events you attend, host, or signal interest in.",
      icon: "📅",
      defaultOptIn: true,
      sensitive: false,
    },
    CREDIBILITY: {
      id: "CREDIBILITY",
      label: "Credibility Data",
      description: "Your Deluzon credibility score and review history.",
      icon: "⭐",
      defaultOptIn: true,
      sensitive: false,
    },
    ATTENTION: {
      id: "ATTENTION",
      label: "Attention Data",
      description: "Likes, shares, suggestions given and received.",
      icon: "👁️",
      defaultOptIn: true,
      sensitive: false,
    },
  };
  
  // ----- Kleido Coin economics -----
  // Kleido Coins are the platform's monetization currency. Their value is dynamic
  // — it depends on overall company performance, individual user contribution,
  // and a few weighted factors below. The exact valuation formula runs
  // server-side; this object documents the public-facing model.
  export const COIN_ECONOMICS = {
    STORAGE_KEY: "monetization.coinBalance",
    CURRENCY_NAME: "Kleido Coins",
    CURRENCY_SYMBOL: "KC",
  
    // Multipliers that the backend uses when calculating how many coins a
    // piece of content earns. Surfaced here so users can see what rewards
    // what behavior.
    MULTIPLIERS: {
      QUALITY: 1.0,        // baseline quality score (0-1) from content analysis
      ATTENTION: 0.5,      // likes + shares + suggestions received
      AD_REVENUE: 2.0,     // ads pulled by the content
      ENGAGEMENT: 0.3,     // comment depth, watch time, replays
      COMMUNITY: 0.7,      // value to community (Deluzon score, Klamity signal)
    },
  
    // Dynamic valuation: coins aren't pegged to a fixed rate. Value depends on:
    //   - Platform-wide revenue and growth (company performance)
    //   - User's historical contribution quality
    //   - Current demand for the user's content category
    // Users see the estimated USD value but actual payouts may differ.
    DYNAMIC_NOTE: "1 KC ≈ $0.001–$0.01 USD, varies with platform performance and your contribution score.",
  };
  
  // ----- Utility: build the default opt-in map from DATA_SOURCES -----
  export const getDefaultDataSourceToggles = () => {
    const map = {};
    Object.values(DATA_SOURCES).forEach((src) => {
      map[src.id] = src.defaultOptIn;
    });
    return map;
  };
  